/**
 * GTM Page Info Push
 * Pushes sh_info + consentUpdate to dataLayer for SPA navigation
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';

const PAGE_TYPE_MAP: Record<string, string> = {
  'index': 'homepage',
  'collection': 'category',
  'product': 'product',
  'cart': 'basket',
  'search': 'searchresults',
};

/**
 * Push sh_info + consentUpdate to GTM dataLayer.
 * sh_info re-activates the "page view" condition in GTM Trigger Groups.
 * consentUpdate re-activates the consent condition.
 */
export function pushGTMPageInfo(analyzify: WindowAnalyzify): void {
  const win = window as any;
  if (!win.dataLayer || !analyzify.properties?.GTM?.status) return;

  const pageType = PAGE_TYPE_MAP[analyzify.shopify_template] || 'other';
  const market = analyzify.market;
  const shop = analyzify.shop;
  const gtmProps = analyzify.properties.GTM;

  // Build user data matching CDN's an_gtm.js format
  const customer = analyzify.shopify_customer;
  const userData = customer?.type === 'visitor'
    ? { type: 'visitor' }
    : { ...customer };

  // Build SSAP vals (Shopify attribution cookie)
  const ssapVals = typeof analyzify.SSAP_vals === 'function'
    ? (analyzify.SSAP_vals() || {})
    : {};

  // 1. Push sh_info — re-activates the "page view" condition in Trigger Groups
  win.dataLayer.push({
    event: 'sh_info',
    ...ssapVals,
    page_type: pageType,
    page_currency: analyzify.currency || null,
    page_id: Date.now(),
    cart_id: analyzify.cart_id || null,
    ss_event_id: analyzify.eventId.get('default') || null,
    shop_locale: shop?.locale || null,
    shop_country: shop?.country || null,
    market_country: market?.country || null,
    market_language: market?.language || null,
    market_handle: market?.handle || null,
    aw_feed_language: gtmProps?.feed_language || null,
    aw_feed_region: gtmProps?.feed_region || null,
    aw_merchant_id: gtmProps?.merchant_id ? Number(gtmProps.merchant_id) : null,
    user: userData,
    ...analyzify.storeObj,
    analyzify_source: 'advanced',
    implementation_type: 'extension',
  });

  // 2. Push consentUpdate — re-activates the consent condition in Trigger Groups.
  //    In Liquid themes, the Analyzify consent layer pushes consentUpdate unconditionally on
  //    every page load. It does NOT check gcm_active or consent_active — Trigger
  //    Groups always need this event regardless of consent configuration.
  //    This is just a dataLayer event; it does NOT affect Google Consent Mode
  //    (that's gtag('consent','update',...) — a separate API).
  const consent = (analyzify as any).current_consent;
  const consentSettings = consent
    ? {
        ...consent,
        analyzify_consent: (
          consent.ad_storage === 'granted' &&
          consent.analytics_storage === 'granted'
        ) ? 'granted' : 'denied',
      }
    : {
        // No consent state resolved yet — default to all granted (no CMP = implied consent)
        ad_storage: 'granted',
        analytics_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        personalization_storage: 'granted',
        functionality_storage: 'granted',
        security_storage: 'granted',
        analyzify_consent: 'granted',
      };

  win.dataLayer.push({
    event: 'consentUpdate',
    status: 'update',
    consentSettings,
  });

  analyzify.log('sh_info + consentUpdate pushed for SPA navigation', 'dispatcher');
}
